import {
  Card,
  Typography,
  Box,
  Chip,
  Divider,
  Stack,
  Grid,
  IconButton,
  Button
} from '@mui/material';
import { formatoFechaAmFm } from '../../../context/DateInstance';
import Iconify from '../../iconify';
import { useAxios } from '../../../context/AxiosHookContext';

const fieldLabels = {
  nombre: 'Nombre',
  cedula: 'Cédula',
  direccion: 'Dirección',
  correo: 'Correo electrónico',
  telefono: 'Teléfono',
  acto: 'Acto administrativo',
  motivo: 'Motivo',
  institucion: 'Institución',
  empresa: 'Empresa',
  responsable: 'Responsable',
  correo_responsable: 'Correo responsable',
  telefono_responsable: 'Teléfono responsable',
  publico: 'Público destinatario',
  cantidad: 'Cantidad de personas',
  tema: 'Tema de interés',
  fechas: 'Fechas y horarios',
  lugar: 'Lugar'
};

export default function TrainingCard({ row, type, subtype, getSolicitanteName, getDetailedData, onUpdate }) {
  const detailData = getDetailedData(row);
  const axios = useAxios();

  const handleMarkAsRead = async () => {
    try {
      await axios.put(`/trainings/mark-read/${row._id}`);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  return (
    <Card 
      sx={{ 
        p: 3, 
        width: '100%',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'stretch', md: 'flex-start' },
        gap: 3,
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-2px)',
          transition: 'all 0.3s ease-in-out'
        }
      }}
    >
      {/* Left Section - Header */}
      <Box sx={{ minWidth: { xs: '100%', md: '250px' } }}>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Chip 
              label={`#${row._id}`} 
              color="primary" 
              variant="filled"
              sx={{ fontWeight: 'bold' }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="caption" color="text.secondary">
                {formatoFechaAmFm(row.created_date)}
              </Typography>
              {!row.read && (
                <IconButton 
                  size="small" 
                  onClick={handleMarkAsRead}
                  sx={{ color: 'success.main' }}
                  title="Marcar como leído"
                >
                  <Iconify icon="eva:checkmark-circle-2-fill" />
                </IconButton>
              )}
            </Stack>
          </Stack>
          
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {getSolicitanteName(row)}
          </Typography>

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Chip 
              label={row.type} 
              variant="outlined" 
              size="small"
              color="secondary"
            />
            <Chip 
              label={row.subType} 
              variant="outlined" 
              size="small"
              color="info"
            />
          </Stack>
        </Stack>
      </Box>

      <Divider orientation={{ xs: 'horizontal', md: 'vertical' }} flexItem />

      {/* Right Section - Details */}
      <Box sx={{ flexGrow: 1 }}>
        <Grid container spacing={2}>
          {row.documento && (
            <Grid item xs={12} sm={6} md={4}>
              <Box>
                <Typography 
                  variant="caption" 
                  color="primary.main" 
                  display="block"
                  sx={{ fontWeight: 600, mb: 0.5 }}
                >
                  Documento
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<Iconify icon="eva:file-text-fill" />}
                  onClick={async () => {
                    try {
                      const response = await axios.get(`/files/${row.documento}`);
                      if (response.data.success) {
                        window.open(response.data.data, '_blank');
                      }
                    } catch (error) {
                      console.error('Error al cargar documento:', error);
                    }
                  }}
                  sx={{ mt: 0.5 }}
                >
                  Ver archivo
                </Button>
              </Box>
            </Grid>
          )}
          
          {Object.entries(detailData).map(([key, value]) => {
            if (!value || key === 'documento') return null;
            
            return (
              <Grid item xs={12} sm={6} md={4} key={key}>
                <Box>
                  <Typography 
                    variant="caption" 
                    color="primary.main" 
                    display="block"
                    sx={{ fontWeight: 600, mb: 0.5 }}
                  >
                    {fieldLabels[key] || key.charAt(0).toUpperCase() + key.slice(1).replace('_', ' ')}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      wordBreak: 'break-word',
                      color: 'text.primary'
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Card>
  );
}